import React from 'react';
import { Box, HStack, Separator, Span, Stack, Text } from '@chakra-ui/react';
import { ColorDTO, RemovedIngredientDTO, Res, SizeDTO, SoldLotDTO, SoldProductDTO } from 'core/api';

export type buildDetailBlocksParams = {
	size: SizeDTO;
	color: ColorDTO;
	addins: SoldProductDTO[];
	removedIngredients: RemovedIngredientDTO[];
	lot: SoldLotDTO;
	notes: string[] | string;
	isProductComposition: boolean;
};

// En el composition de producto las notas no son una lista, si no un string con saltos de línea.
const formatPreparationStringNotes = (notes: string): React.ReactElement => {
	return <Box fontSize={'xs'} fontWeight={'semibold'}>
		{notes.split('\n').map((line, index) =>
			<DetailRow
				key={index}
				label={Res.Web.TICKET_LINES_CONTROL_NOTE_LBL()}
				value={line}
			/>
		)}
	</Box>;
};

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
	return (
		<HStack align="start" gap="2" w="full" lineHeight="120%">
			{label ? (
				<>
					<Text fontSize="xs" fontWeight="semibold" whiteSpace="nowrap">
						{label}
					</Text>
					<Span fontWeight={'normal'}>
						{' - '}
					</Span>
				</>
			) : (
				<Box w="0" />
			)}
			<Text
				fontSize="xs"
				fontWeight="normal"
				color={{ base: 'neutral.600', _dark: 'neutral.300' }}
				minW="0"
			>
				{value}
			</Text>
		</HStack>
	);
}

function getSaleFormatName(soldProducts: SoldProductDTO[]): string {
	return soldProducts.length > 1 ? `${soldProducts[0].SaleFormatName} (x${soldProducts.length})` : soldProducts[0].SaleFormatName;
}

export function buildDetailBlocks({ size, color, addins, removedIngredients, lot, notes, isProductComposition }: buildDetailBlocksParams): React.ReactNode | null {
	const blocks: React.ReactNode[] = [];

	// Size / Color
	if (size?.Id > 0 || color?.Id > 0) {
		blocks.push(
			<Box key="size-color" w="full">
				<DetailRow
					key={`product-size-${size.Id}`}
					label={Res.Web.COMPOSE_PRODUCT_SIZE_LBL()}
					value={size.Name}
				/>
				<DetailRow
					key={`product-color-${color.Id}`}
					label={Res.Web.COMPOSE_PRODUCT_COLOR_LBL()}
					value={color.Name}
				/>
			</Box>
		);
	}

	// Addins
	if (addins?.length) {
		blocks.push(
			<Box key="addins" w="full">
				{addins
					.groupBy(x => x.SaleFormatId)
					.map((addin, index) => (
						<DetailRow
							key={`addin-${index}`}
							label={Res.Web.TICKET_LINES_CONTROL_PRODUCT_WITH_LBL()}
							value={getSaleFormatName(addin.value)}
						/>
					))}
			</Box>
		);
	}

	// Removed ingredients
	if (removedIngredients?.length) {
		blocks.push(
			<Box key="removed" w="full">
				{removedIngredients.map((ingredient, index) => (
					<DetailRow
						key={`removed-${index}`}
						label={Res.Web.TICKET_LINES_CONTROL_PRODUCT_WITHOUT_LBL()}
						value={ingredient.Style.Text}
					/>
				))}
			</Box>
		);
	}

	// Lots
	if (lot?.Id > 0) {
		blocks.push(
			<Box key="lots" w="full">
				<Text key={`lot-${lot.Id}`} fontSize="xs" fontWeight="semibold">
					{Res.Web.TICKET_LINES_CONTROL_PRODUCT_LOT_LBL(lot.Number)}
				</Text>
			</Box>
		);
	}

	// Notes
	if (notes?.length) {
		blocks.push(
			<Box key="notes" w="full">
				{isProductComposition
					? formatPreparationStringNotes(notes as string)
					: (notes as string[]).map((note, index) => (
						<DetailRow
							key={`note-${index}`}
							label={Res.Web.TICKET_LINES_CONTROL_NOTE_LBL()}
							value={note}
						/>
					))
				}
			</Box>
		);
	}

	if (!blocks.length) return null;

	return (
		<Stack
			pl={isProductComposition ? '15' : '20'}
			gap="0.5"
			w="full"
			color={{ base: 'neutral.800', _dark: 'neutral.100' }}
		>
			{blocks.map((block, idx) => (
				<React.Fragment key={idx}>
					{block}
					{idx < blocks.length - 1 && (
						<Separator
							w="full"
							borderColor={{ base: 'neutral.200', _dark: 'neutral.700' }}
						/>
					)}
				</React.Fragment>
			))}
		</Stack>
	);
}